/**
 * User Permission Assignment Page
 * Phase 5: RBAC & Permission System - M4.T4.2
 * 
 * Dual list interface for assigning permission groups to users
 * Features: Available ↔ Assigned transfer list
 */

import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Grid,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import permissionGroupService from '../../services/permissionGroupService';
import alertify from 'alertifyjs';

const UserPermissionAssignmentPage = () => {
  const { userId } = useParams();
  const history = useHistory();
  
  const [user, setUser] = useState(null);
  const [available, setAvailable] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [selectedAvailable, setSelectedAvailable] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        alertify.error('Kullanıcı ID bulunamadı');
        history.push('/users');
        return;
      }
      
      setIsLoading(true);
      try {
        const data = await permissionGroupService.getUserPermissionGroups(userId);
        setUser(data.user);
        setAvailable(data.available || []);
        setAssigned(data.assigned || []);
      } catch (error) {
        console.error('Error fetching user permission groups:', error);
        alertify.error('Yetki grupları yüklenemedi');
        history.push('/users');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [userId, history]);
  
  // Move selected groups from available to assigned
  const handleMoveRight = () => {
    const toMove = available.filter(g => selectedAvailable.includes(g._id));
    setAssigned([...assigned, ...toMove]);
    setAvailable(available.filter(g => !selectedAvailable.includes(g._id)));
    setSelectedAvailable([]);
  };
  
  // Move selected groups from assigned to available
  const handleMoveLeft = () => {
    const toMove = assigned.filter(g => selectedAssigned.includes(g._id));
    setAvailable([...available, ...toMove]);
    setAssigned(assigned.filter(g => !selectedAssigned.includes(g._id)));
    setSelectedAssigned([]);
  };
  
  // Save assignment
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const groupIds = assigned.map(g => g._id);
      await permissionGroupService.assignPermissionGroups(userId, groupIds);
      alertify.success('Yetki grupları başarıyla atandı. Değişiklikler hemen yansıyacak.');
      history.push('/users');
    } catch (error) {
      console.error('Error assigning permission groups:', error);
      alertify.error('Yetki grupları atanırken hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Calculate total unique permissions
  const totalPermissions = new Set(
    assigned.flatMap(g => g.permissions?.map(p => p._id) || [])
  ).size;
  
  // Get user type label
  const getUserTypeLabel = (userType) => {
    if (userType === 'company_owner') return 'Şirket Sahibi';
    if (userType === 'company_member') return 'Şirket Üyesi';
    if (userType === 'individual') return 'Bireysel';
    if (userType === 'system_admin') return 'Sistem Yöneticisi';
    return userType || 'Bilinmeyen';
  };
  
  // Get user type color
  const getUserTypeColor = (userType) => {
    if (userType === 'company_owner') return 'primary';
    if (userType === 'system_admin') return 'error';
    return 'default';
  };
  
  // Loading state
  if (isLoading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
    );
  }
  
  return (
      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <Button
            variant="outlined"
            onClick={() => history.push('/users')}
          >
            ← Geri
          </Button>
          <Typography variant="h4" fontWeight="bold">
            👤 {user?.firstName} {user?.lastName} - Yetki Grupları Ata
          </Typography>
        </Stack>
        
        {/* User Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={3} alignItems="center">
              <Chip icon={<EmailIcon />} label={user?.email} variant="outlined" />
              <Chip 
                icon={<BusinessIcon />} 
                label={getUserTypeLabel(user?.userType)} 
                color={getUserTypeColor(user?.userType)} 
              />
            </Stack>
          </CardContent>
        </Card>
        
        {/* Dual List Card */}
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              {/* Available Groups (Left) */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper variant="outlined" sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    📦 Mevcut Gruplar ({available.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <List dense>
                      {available.map(group => (
                        <ListItem
                          key={group._id}
                          button
                          onClick={() => {
                            setSelectedAvailable(prev =>
                              prev.includes(group._id)
                                ? prev.filter(id => id !== group._id)
                                : [...prev, group._id]
                            );
                          }}
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedAvailable.includes(group._id)}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight="medium">
                                {group.name}
                              </Typography>
                            }
                            secondary={
                              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                <Chip label={`${group.permissions?.length || 0} yetki`} size="small" />
                              </Stack>
                            }
                          />
                        </ListItem>
                      ))}
                      {available.length === 0 && (
                        <Box sx={{ py: 8, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Atanabilecek grup yok
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Box>
                </Paper>
              </Grid>
              
              {/* Move Buttons (Center) */}
              <Grid size={{ xs: 12, md: 2 }}>
                <Stack
                  direction={{ xs: 'row', md: 'column' }}
                  spacing={2}
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: '100%' }}
                >
                  <Button
                    variant="contained"
                    onClick={handleMoveRight}
                    disabled={selectedAvailable.length === 0}
                    sx={{ minWidth: 100 }}
                  >
                    <ArrowForwardIcon />
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleMoveLeft}
                    disabled={selectedAssigned.length === 0}
                    sx={{ minWidth: 100 }}
                  >
                    <ArrowBackIcon />
                  </Button>
                </Stack>
              </Grid>
              
              {/* Assigned Groups (Right) */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper variant="outlined" sx={{ p: 2, height: 450, display: 'flex', flexDirection: 'column', bgcolor: 'success.50' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    ✅ Atanmış Gruplar ({assigned.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <List dense>
                      {assigned.map(group => (
                        <ListItem
                          key={group._id}
                          button
                          onClick={() => {
                            setSelectedAssigned(prev =>
                              prev.includes(group._id)
                                ? prev.filter(id => id !== group._id)
                                : [...prev, group._id]
                            );
                          }}
                          sx={{
                            borderRadius: 1,
                            mb: 0.5,
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedAssigned.includes(group._id)}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight="medium">
                                {group.name}
                              </Typography>
                            }
                            secondary={
                              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                <Chip label={`${group.permissions?.length || 0} yetki`} size="small" color="success" />
                              </Stack>
                            }
                          />
                        </ListItem>
                      ))}
                      {assigned.length === 0 && (
                        <Box sx={{ py: 8, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            Henüz grup atanmamış
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                            Sol taraftaki gruplardan seçim yapın
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
            
            {/* Summary Alert */}
            {assigned.length > 0 && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  💡 <strong>Toplam {totalPermissions} farklı yetki</strong> atanacak (Çakışan yetkiler otomatik birleştirildi)
                </Typography>
              </Alert>
            )}
            
            {/* Actions */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => history.push('/users')}
                disabled={isSaving}
              >
                İptal
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Kaydediliyor...' : '✅ Kaydet ve Uygula'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Container>
  );
};

export default UserPermissionAssignmentPage;

